// Şub 27, 2026 — DPD Günlük Özet sayfası
// Fast-Trans firmasına ait DPD tur/kurye verilerini gösterir.
import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { downloadDocument } from '../../lib/documentPreview'; // no-op unused import removed later
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Truck, Package, MapPin, Clock, RefreshCw, DownloadCloud, Loader2, TrendingUp,
  Sunrise, AlertCircle, Share2, AlertTriangle, FileX, SlidersHorizontal, ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuCheckboxItem,
} from '../../components/ui/dropdown-menu';

// eslint-disable-next-line no-unused-vars
const _keep_downloadDocument = downloadDocument;
import TodayIncompleteBanner from './TodayIncompleteBanner';
import DPDTourDetailDrawer from './DPDTourDetailDrawer';

const MISSING_LABELS = {
  route_datalog: 'RouteDataLog (akşam 23:45)',
  retour_report: 'Retour Raporu (öğleden sonra)',
  stops_parcels: 'Stops & Parcels (sabah)',
};

export default function DPDDailySummary() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  // Task 1 — Tur detay drawer
  const [drawerTour, setDrawerTour] = useState(null);
  // Şub 28, 2026 — Kolon görünürlük + sıralama
  const [sortKey, setSortKey] = useState('tour');
  const [sortDir, setSortDir] = useState('asc');
  const COLUMN_DEFS = useMemo(() => [
    { key: 'tour',            label: 'Tur',       def: true },
    { key: 'courier',         label: 'Kurye',     def: true },
    { key: 'chassis',         label: 'Plaka/Şasi', def: true },
    { key: 'stops',           label: 'Stops',     def: true, align: 'right' },
    { key: 'parcels',         label: 'Paket',     def: true, align: 'right' },
    { key: 'retour',          label: 'Retour',    def: true, align: 'right' },
    { key: 'delivery_rate',   label: 'Teslim %',  def: false, align: 'right' },
    { key: 'planned',         label: 'Beklenen',  def: true, align: 'right' },
    { key: 'variance',        label: 'Varyans',   def: true },
    { key: 'km',              label: 'Km',        def: true, align: 'right' },
    { key: 'duration',        label: 'Süre',      def: true },
    { key: 'start',           label: 'Başlangıç', def: true },
    { key: 'end',             label: 'Bitiş',     def: true },
    { key: 'revenue',         label: 'Gelir (€)', def: false, align: 'right' },
    { key: 'share',           label: 'Paylaş',    def: true, align: 'right', pinned: true },
  ], []);
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('dpd_daily_summary_cols') || 'null');
      if (Array.isArray(saved)) return new Set(saved);
    } catch (e) { /* ignore */ }
    return new Set(['tour','courier','chassis','stops','parcels','retour','planned','variance','km','duration','start','end','share']);
  });
  useEffect(() => {
    try { localStorage.setItem('dpd_daily_summary_cols', JSON.stringify(Array.from(visibleCols))); } catch (e) { /* ignore */ }
  }, [visibleCols]);
  const toggleCol = (k) => setVisibleCols((prev) => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'tour' ? 'asc' : 'desc'); }
  };
  // Task 3 — WhatsApp export helper
  const copyTourToWhatsApp = (r) => {
    const dayText = selectedDate;
    let variance = '';
    if (r.variance_label && r.variance_kind !== 'unknown') {
      if (r.variance_kind === 'over') variance = `\n⚠️ Beklenenden %${Math.abs(r.variance_pct)} fazla`;
      else if (r.variance_kind === 'under') variance = `\n⚠️ Beklenenden %${Math.abs(r.variance_pct)} eksik`;
      else variance = `\n✅ Plana uygun`;
    }
    const planned = r.planned_parcels
      ? (r.planned_stops
          ? `Beklenen: ${r.planned_parcels} paket / ${r.planned_stops} stop\n`
          : `Beklenen: ${r.planned_parcels} paket\n`)
      : `Beklenen: — (plan yok)\n`;
    const msg = `📦 ${r.tour} — ${r.courier_name || 'Şoför'}\n${dayText}\n${planned}Gerçek: ${r.deliveries_parcels} paket / ${r.total_stops || r.deliveries_stops} stop${variance}`;
    navigator.clipboard.writeText(msg).then(() => {
      toast.success(`${r.tour} WhatsApp formatında kopyalandı`);
    }).catch(() => toast.error('Kopyalanamadı'));
  };
  const copyDaySummary = () => {
    if (!data) return;
    const t = data.totals;
    const lines = [
      `📊 Fast-Trans DPD Özeti — ${selectedDate}`,
      `Turlar: ${t.tours} | Paket: ${t.total_parcels} | Stops: ${t.total_stops}`,
    ];
    if (t.planned_parcels) {
      const diff = t.total_parcels - t.planned_parcels;
      const sign = diff >= 0 ? '+' : '';
      lines.push(`Beklenen: ${t.planned_parcels} paket → ${sign}${diff} fark`);
    }
    if (t.retour_parcels) lines.push(`Retour: ${t.retour_parcels}`);
    lines.push(`Gelir: €${t.revenue_eur}`);
    navigator.clipboard.writeText(lines.join('\n')).then(() => toast.success('Gün özeti kopyalandı'));
  };

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      // Default: Fast-Trans firmasını seç (isim ile match)
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    }).catch(() => {});
  }, []);

  const fetchDates = useCallback(async () => {
    if (!firmId) return;
    try {
      const r = await api.get(`/dpd/dates?firm_id=${firmId}`);
      const ds = r.data?.data || [];
      setDates(ds);
      if (ds.length > 0 && !selectedDate) setSelectedDate(ds[0]);
      else if (ds.length === 0) { setSelectedDate(''); setData(null); }
    } catch (e) { console.error(e); }
  }, [firmId, selectedDate]);

  useEffect(() => { fetchDates(); }, [fetchDates]);

  const fetchSummary = useCallback(async () => {
    if (!firmId || !selectedDate) return;
    setLoading(true);
    try {
      const r = await api.get(`/dpd/daily-summary?firm_id=${firmId}&date=${selectedDate}`);
      setData(r.data?.data);
    } catch (e) {
      console.error(e);
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId, selectedDate]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const ingest = async () => {
    if (!firmId) return;
    setIngesting(true);
    try {
      const r = await api.post(`/dpd/ingest-from-archive?firm_id=${firmId}`);
      const d = r.data?.data;
      toast.success(`${d.ingested} dosya işlendi${d.errors?.length ? `, ${d.errors.length} hata` : ''}`);
      await fetchDates();
      await fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İçe aktarma başarısız');
    } finally {
      setIngesting(false);
    }
  };

  const totals = data?.totals || {};
  const rawRows = data?.route_data || [];
  const rows = useMemo(() => {
    const arr = [...rawRows];
    const sortAcc = {
      tour: (r) => r.tour || '',
      courier: (r) => r.courier_name || '',
      chassis: (r) => r.chassis_nbr || '',
      stops: (r) => r.deliveries_stops || 0,
      parcels: (r) => r.deliveries_parcels || 0,
      retour: (r) => r.retour_parcels || 0,
      delivery_rate: (r) => r.delivery_rate || 0,
      planned: (r) => r.planned_parcels || 0,
      variance: (r) => r.variance_pct || 0,
      km: (r) => r.distance_km || 0,
      duration: (r) => r.working_hours || 0,
      start: (r) => r.start_time_fmt || '',
      end: (r) => r.end_time_fmt || '',
      revenue: (r) => r.revenue_eur || 0,
    };
    const acc = sortAcc[sortKey] || sortAcc.tour;
    arr.sort((a, b) => {
      const av = acc(a); const bv = acc(b);
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [rawRows, sortKey, sortDir]);
  const stopsMap = useMemo(() => {
    const m = new Map();
    (data?.stops || []).forEach((s) => m.set(s.tour, s));
    return m;
  }, [data]);
  const debriefMap = useMemo(() => {
    const m = new Map();
    (data?.debrief || []).forEach((d) => m.set(d.tour, d));
    return m;
  }, [data]);

  const fmtDur = (sec) => {
    if (!sec) return '-';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}s ${m}dk`;
  };

  return (
    <div className="space-y-4" data-testid="dpd-daily-summary-page">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-orange-600" />{t('dpd.dailySummary.title')}
            </CardTitle>
            <CardDescription>{t('dpd.dailySummary.subtitle')}</CardDescription>
          </div>
          <Button onClick={ingest} disabled={ingesting} variant="outline" data-testid="dpd-ingest-btn">
            {ingesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DownloadCloud className="h-4 w-4 mr-2" />}
            {t('dpd.dailySummary.ingestFromMail')}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="dpd-firm-select"><SelectValue placeholder={t('dpd.common.firm')} /></SelectTrigger>
              <SelectContent>
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedDate} onValueChange={setSelectedDate}>
              <SelectTrigger data-testid="dpd-date-select"><SelectValue placeholder={t('dpd.common.date')} /></SelectTrigger>
              <SelectContent>
                {dates.length === 0 && <div className="p-2 text-xs text-gray-500">{t('dpd.common.noData')}</div>}
                {dates.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={fetchSummary} disabled={loading} variant="outline">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {t('dpd.common.refresh')}
            </Button>
            {/* Task 3 — Gün özetini WhatsApp formatında kopyala */}
            <Button onClick={copyDaySummary} disabled={!data} variant="outline" size="sm" className="h-9" data-testid="dpd-day-share-btn">
              <Share2 className="h-3.5 w-3.5 mr-1.5" />Paylaş
            </Button>
          </div>

          {/* Şub 27, 2026 — Bugün için veri eksikliği bilgilendirmesi */}
          <TodayIncompleteBanner date={selectedDate} variant="daily" />

          {/* Task 4 — Bugünün beklenen 3 dosyasından hangileri eksik (ingest edilmedi) */}
          {data?.totals?.missing_reports?.length > 0 && (
            <div
              className="mb-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 flex items-start gap-2.5"
              data-testid="dpd-missing-reports-banner"
            >
              <FileX className="h-4 w-4 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-[12px]">
                <div className="font-semibold text-red-900 dark:text-red-200">
                  Bu gün için eksik rapor(lar): {data.totals.missing_reports.length}
                </div>
                <ul className="mt-1 space-y-0.5 text-red-800 dark:text-red-300 list-disc list-inside">
                  {data.totals.missing_reports.map((k) => (
                    <li key={k}>{MISSING_LABELS[k] || k}</li>
                  ))}
                </ul>
                <div className="text-[10px] mt-1 opacity-80">
                  Farklı isim veya farklı gönderenden gelmiş olabilir — <a href="/dashboard/dpd/missing" className="underline font-semibold">Eksik Dosyalar sayfası</a>'ndan tara.
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
            <StatCard icon={Truck} label={t('dpd.dailySummary.stats.tours')} value={totals.tours || 0} color="orange" />
            <StatCard icon={MapPin} label={t('dpd.dailySummary.stats.totalStops')} value={totals.total_stops || 0} color="blue" />
            <StatCard icon={Package} label={t('dpd.dailySummary.stats.delivered')} value={totals.total_parcels || 0} color="green" />
            <StatCard icon={TrendingUp} label={t('dpd.dailySummary.stats.totalKm')} value={totals.total_km || 0} color="purple" />
            <StatCard icon={Package} label={t('dpd.dailySummary.stats.retour')} value={totals.retour_parcels || 0} color="red" />
            {/* Şub 27, 2026 — Günün ortalama çalışma saati istatistikleri */}
            <StatCard icon={Sunrise} label="Ort. Başlangıç" value={totals.avg_start_time || '-'} color="amber" />
            <StatCard icon={Clock} label="Ort. Saat/Tur" value={totals.avg_working_hours ? `${totals.avg_working_hours}h` : '-'} color="indigo" />
          </div>

          <div className="rounded-lg border overflow-x-auto">
            {/* Kolon filtre + sort ipuçları toolbar */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border-b text-xs text-gray-600">
              <div>{rawRows.length} tur — kolonlara tıklayarak sırala</div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 gap-1" data-testid="dpd-cols-toggle">
                    <SlidersHorizontal className="h-3 w-3" /> Kolonlar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel>Görünür Kolonlar</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {COLUMN_DEFS.filter((c) => !c.pinned).map((c) => (
                    <DropdownMenuCheckboxItem
                      key={c.key}
                      checked={visibleCols.has(c.key)}
                      onCheckedChange={() => toggleCol(c.key)}
                      data-testid={`dpd-col-${c.key}`}
                    >
                      {c.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  {COLUMN_DEFS.map((c) => {
                    if (!visibleCols.has(c.key) && !c.pinned) return null;
                    const sortable = c.key !== 'share' && c.key !== 'variance';
                    return (
                      <TableHead
                        key={c.key}
                        className={`${c.align === 'right' ? 'text-right' : ''} ${sortable ? 'cursor-pointer select-none' : ''}`}
                        onClick={sortable ? () => toggleSort(c.key) : undefined}
                      >
                        <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'justify-end' : ''}`}>
                          {c.label}
                          {sortable && (
                            sortKey === c.key
                              ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
                              : <ArrowUpDown className="h-3 w-3 opacity-30" />
                          )}
                        </span>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && Array.from({length:5}).map((_,i)=>(
                  <TableRow key={`sk-${i}`}>{Array.from({length: visibleCols.size + 1}).map((__,j)=>(
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}</TableRow>
                ))}
                {!loading && rows.length === 0 && (
                  <TableRow><TableCell colSpan={visibleCols.size + 1} className="text-center text-gray-500 py-8">
                    {t('dpd.consolidated.noTourData')}
                  </TableCell></TableRow>
                )}
                {!loading && rows.map((r) => {
                  const d = debriefMap.get(r.tour);
                  const variantColor = r.variance_kind === 'over'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : r.variance_kind === 'under'
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                    : r.variance_kind === 'match'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                  const rateChip = r.delivery_rate >= 95 ? 'bg-emerald-100 text-emerald-700'
                    : r.delivery_rate >= 90 ? 'bg-green-100 text-green-700'
                    : r.delivery_rate >= 85 ? 'bg-yellow-100 text-yellow-700'
                    : r.delivery_rate >= 80 ? 'bg-orange-100 text-orange-700'
                    : 'bg-red-100 text-red-700';
                  return (
                    <TableRow
                      key={r.id}
                      data-testid={`dpd-tour-${r.tour}`}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/40"
                      onClick={(e) => {
                        if (e.target.closest('[data-share-btn]')) return;
                        setDrawerTour(r.tour);
                      }}
                    >
                      {visibleCols.has('tour') && (
                        <TableCell><Badge variant="outline" className="font-mono">{r.tour}</Badge></TableCell>
                      )}
                      {visibleCols.has('courier') && (
                        <TableCell className="truncate max-w-[180px]" title={r.courier_name}>{r.courier_name || '-'}</TableCell>
                      )}
                      {visibleCols.has('chassis') && (
                        <TableCell className="text-xs text-gray-600 truncate max-w-[110px]" title={d?.numberplate || r.chassis_nbr}>
                          {d?.numberplate || r.chassis_nbr?.slice(-8) || '-'}
                        </TableCell>
                      )}
                      {visibleCols.has('stops') && (
                        <TableCell className="text-right font-medium">{r.deliveries_stops}</TableCell>
                      )}
                      {visibleCols.has('parcels') && (
                        <TableCell className="text-right font-medium">{r.deliveries_parcels}</TableCell>
                      )}
                      {visibleCols.has('retour') && (
                        <TableCell className="text-right">
                          {r.retour_parcels ? (
                            <Badge variant="outline" className={`font-mono ${r.retour_parcels >= 10 ? 'text-red-700 border-red-300' : 'text-gray-700'}`}>
                              {r.retour_parcels}
                            </Badge>
                          ) : <span className="text-gray-400">-</span>}
                        </TableCell>
                      )}
                      {visibleCols.has('delivery_rate') && (
                        <TableCell className="text-right">
                          <span className={`inline-block px-1.5 rounded text-xs font-medium ${rateChip}`}>
                            %{r.delivery_rate || 0}
                          </span>
                        </TableCell>
                      )}
                      {visibleCols.has('planned') && (
                        <TableCell className="text-right text-xs text-gray-600" title="Beklenen: Stops&Parcels planı">
                          {r.planned_parcels
                            ? (r.planned_stops ? `${r.planned_parcels}/${r.planned_stops}` : r.planned_parcels)
                            : '-'}
                        </TableCell>
                      )}
                      {visibleCols.has('variance') && (
                        <TableCell>
                          {r.variance_label && (
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${variantColor}`}>
                              {r.variance_label}
                            </span>
                          )}
                        </TableCell>
                      )}
                      {visibleCols.has('km') && (
                        <TableCell className="text-right">{r.distance_km || '-'}</TableCell>
                      )}
                      {visibleCols.has('duration') && (
                        <TableCell>
                          {r.working_hours ? (
                            <span className={r.flag_overtime ? 'px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 font-semibold text-xs inline-flex items-center gap-0.5' : 'text-xs'}>
                              {fmtDur(r.total_sec) !== '-' ? fmtDur(r.total_sec) : `${r.working_hours}h`}
                              {r.flag_overtime && <AlertCircle className="h-3 w-3" />}
                            </span>
                          ) : fmtDur(r.total_sec)}
                        </TableCell>
                      )}
                      {visibleCols.has('start') && (
                        <TableCell className="text-xs font-mono">
                          {r.start_time_fmt ? (
                            <span className={r.flag_late_start ? 'px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 inline-flex items-center gap-0.5' : ''}>
                              {r.start_time_fmt}
                              {r.flag_late_start && <AlertCircle className="h-3 w-3" title="Geç başlangıç (09:00+)" />}
                            </span>
                          ) : '-'}
                        </TableCell>
                      )}
                      {visibleCols.has('end') && (
                        <TableCell className="text-xs font-mono">{r.end_time_fmt || '-'}</TableCell>
                      )}
                      {visibleCols.has('revenue') && (
                        <TableCell className="text-right text-xs text-gray-600">
                          {r.revenue_eur ? `€${r.revenue_eur}` : '-'}
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <Button
                          data-share-btn="1"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          title="WhatsApp formatında kopyala"
                          onClick={(e) => { e.stopPropagation(); copyTourToWhatsApp(r); }}
                          data-testid={`dpd-share-${r.tour}`}
                        >
                          <Share2 className="h-3.5 w-3.5 text-emerald-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      {/* Task 1 — Tur detay drawer */}
      <DPDTourDetailDrawer
        open={!!drawerTour}
        onOpenChange={(o) => { if (!o) setDrawerTour(null); }}
        firmId={firmId}
        date={selectedDate}
        tour={drawerTour}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    orange: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    indigo: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300',
  };
  return (
    <div className={`rounded-lg p-3 ${colors[color]}`}>
      <div className="flex items-center gap-2 text-xs opacity-80"><Icon className="h-4 w-4" />{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
